import { Link } from "react-router-dom";
import { HIPAABadge } from "@/components/HIPAACompliance";
import LanguageToggle from "@/components/LanguageToggle";

/**
 * Public-facing footer with hyperlinks. Used on the landing page and across
 * patient-side authenticated pages so legal/help links are always reachable.
 *
 * IMPORTANT: this is the single source of truth for footer links. The
 * inline footer in LandingPage.js mirrors this structure 1:1 — keep them
 * in sync when adding new links.
 *
 * Variants:
 *   - "light" (default) → for white/slate backgrounds (landing, dashboards)
 *   - "dark"            → for dark sections (none currently, but ready)
 */
export default function PublicFooter({ variant = "light" }) {
  const dark = variant === "dark";
  const linkCls = dark
    ? "text-slate-300 hover:text-white"
    : "text-slate-500 hover:text-slate-900";
  const containerCls = dark
    ? "bg-slate-900 border-t border-slate-800"
    : "bg-white border-t border-slate-200";
  const headingCls = dark
    ? "text-slate-100"
    : "text-slate-900";
  const tinyCls = dark
    ? "text-slate-500"
    : "text-slate-400";

  return (
    <footer className={`${containerCls} mt-12`} data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 sm:grid-cols-4 gap-8">
        <div className="col-span-2 sm:col-span-1">
          <p className={`text-lg font-bold ${headingCls}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
            GLP<span className="text-purple-600">Flo</span>
          </p>
          <p className={`text-xs ${tinyCls} mt-2 leading-relaxed`}>
            Telehealth GLP-1 therapy. Licensed providers. Real medications. Real results.
          </p>
          <p className={`text-[11px] mt-2 leading-relaxed font-bold ${dark ? "text-emerald-300" : "text-emerald-700"}`}>
            🇺🇸 100% US-based clinical care team.
          </p>
          <div className="mt-3">
            <HIPAABadge variant="footer" />
          </div>
        </div>

        <div>
          <p className={`text-xs uppercase tracking-wider font-semibold ${headingCls} mb-3`}>Patients</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/login" className={linkCls} data-testid="footer-login">Sign in</Link></li>
            <li><Link to="/register" className={linkCls} data-testid="footer-register">Get started</Link></li>
            <li><Link to="/pricing" className={linkCls} data-testid="footer-pricing">Pricing &amp; medications</Link></li>
            <li><Link to="/browse-medications" className={linkCls} data-testid="footer-browse-medications">Browse medications</Link></li>
            <li><Link to="/forgot-password" className={linkCls} data-testid="footer-forgot-password">Forgot password?</Link></li>
            <li><a href="https://www.youtube.com/channel/UC2NOTygbPH-3JCkElwzNEWw" target="_blank" rel="noopener noreferrer" className={linkCls} data-testid="footer-youtube">GLPFlo on YouTube</a></li>
            <li><Link to="/coa" className={linkCls} data-testid="footer-coa">Verify your vial (COA lookup)</Link></li>
          </ul>
        </div>

        <div>
          <p className={`text-xs uppercase tracking-wider font-semibold ${headingCls} mb-3`}>Company</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/our-physicians" className={linkCls} data-testid="footer-our-physicians">Our Physicians</Link></li>
            <li><Link to="/pharmacy-info" className={linkCls} data-testid="footer-pharmacy-info">Pharmacy Info</Link></li>
            <li><Link to="/faq" className={linkCls} data-testid="footer-faq">FAQ</Link></li>
            <li><Link to="/stepathon" className={linkCls} data-testid="footer-stepathon">Step-a-Thon</Link></li>
            <li><a href="/#services" className={linkCls} data-testid="footer-services">Services</a></li>
            <li><Link to="/pharmacy-affiliate" className={`${linkCls} font-medium`} data-testid="footer-pharmacy-affiliate">Want to be a pharmacy affiliate?</Link></li>
          </ul>
        </div>

        <div>
          <p className={`text-xs uppercase tracking-wider font-semibold ${headingCls} mb-3`}>Legal</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/privacy-policy" className={linkCls} data-testid="footer-privacy">Privacy Policy</Link></li>
            <li><Link to="/terms-of-service" className={linkCls} data-testid="footer-terms">Terms of Service</Link></li>
            <li><Link to="/online-tracking" className={linkCls} data-testid="footer-online-tracking">Online tracking disclosure</Link></li>
            <li><button
              type="button"
              onClick={() => {
                try { localStorage.removeItem("glpflo_cookie_consent_v1"); } catch { /* noop */ }
                window.location.reload();
              }}
              className={`${linkCls} text-left`}
              data-testid="footer-cookie-settings"
            >Cookie settings</button></li>
            <li><Link to="/shipping-study" className={linkCls} data-testid="footer-shipping-study">Shipping Stability Study</Link></li>
            <li><Link to="/hipaa-compliance" className={linkCls} data-testid="footer-hipaa-compliance">HIPAA Compliance</Link></li>
            <li><a href="tel:+17275162142" className={linkCls} data-testid="footer-phone">(727) 516-2142</a></li>
            <li><a href="tel:+18887743320" className={linkCls} data-testid="footer-phone-secondary">(888) 774-3320</a></li>
            <li><a href="mailto:customer.service@glpflo.com" className={linkCls} data-testid="footer-email">customer.service@glpflo.com</a></li>
          </ul>
        </div>
      </div>

      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3`}>
        <div className="flex items-center gap-3">
          <LanguageToggle />
          <p className={`text-[11px] ${tinyCls}`}>Choose language</p>
        </div>
        <div className={`text-[11px] flex flex-col sm:flex-row sm:items-center gap-3 ${tinyCls}`}>
          <p>© {new Date().getFullYear()} GLPFlo LLC. All rights reserved.</p>
          <p>Compounded medications dispensed by licensed 503A / 503B pharmacy partners.</p>
        </div>
      </div>
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4 text-[11px] leading-relaxed ${tinyCls}`}>
        <p data-testid="footer-fda-disclosure">
          Compounded semaglutide/tirzepatide is prepared by a state-licensed 503A/503B pharmacy and is not FDA-approved.{" "}
          <Link
            to="/shipping-study"
            className="text-purple-600 hover:text-purple-800 underline"
            data-testid="footer-fda-shipping-study-link"
          >
            See the shipping stability study
          </Link>
          . Not affiliated with Novo Nordisk or Eli Lilly.
        </p>
      </div>
    </footer>
  );
}
